'use client'

import { FormEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLocale, useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  FileIcon,
  GripVerticalIcon,
  ImageIcon,
  LanguagesIcon,
  MinusIcon,
  PackageIcon,
  PencilRulerIcon,
  PlusIcon,
  PlusCircleIcon,
  SaveIcon,
  ToolboxIcon,
  XIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { MoneyInput } from '@/components/ui/money-input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Editor } from '@/components/ui/editor'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { FileSelector } from '@/components/partials/files'
import { useMetaStore } from '@/store'
import { useBrands, useCategory, useModels, useScrollToError, useUnsavedChangesGuard, useVehicleGroups } from '@/hooks'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import {
  CreateProductParams,
  ProductFileParams,
  ProductOemParams,
  ProductPictureParams,
  ProductPropertyParams,
  ProductTranslationParam,
  ProductTranslationsParam,
  ProductType
} from '@/types/product'
import { FileType } from '@/types/file'
import { FormModeEnum } from '@/enums'
import { CategoryCombobox } from '@/components/ui/category-combobox'
import { PropertyFieldsData, PropertyFieldsForm } from './'
import { ProductCombobox } from '@/components/ui/product-combobox'
import { setValidationErrors } from '@/lib/utils'
import { UnsavedChangesGuardDialog } from '@/components/partials'

type PropsType = {
  initialData?: ProductType | null
  onSubmit: (data: CreateProductParams) => void
  onSubmitAndNew?: (data: CreateProductParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type ProductFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type OemNumberEntry = {
  id: string // unique id for dnd-kit
  vehicleGroupId: string
  brandId: string
  modelId: string
  oem: string
}

type FormData = {
  categoryId: string
  additionalCategoryIds: string[]
  code: string
  oemNumbers: OemNumberEntry[]
  active: boolean
  price: string
  taxRate: string
  priceIncludesTax: boolean
  discountRate: string
  stock: string
  weight?: string
  unitsPerBox?: string
  boxLength?: string
  boxWidth?: string
  boxHeight?: string
  boxDimensionalWeight?: string
  translations: ProductTranslationsParam
  properties: PropertyFieldsData[]
  repairKitIds: string[]
  repairForIds: string[]
}

// Sortable OEM Number Item component
type SortableOemNumberItemProps = {
  entry: OemNumberEntry
  index: number
  vehicleGroupOptions: ComboboxOption[]
  brandOptions: ComboboxOption[]
  modelOptions: ComboboxOption[]
  onUpdateField: (index: number, field: keyof Omit<OemNumberEntry, 'id'>, value: string) => void
  onRemove: (index: number) => void
  t: (key: string) => string
  errors?: {
    vehicleGroupId?: string
    brandId?: string
    modelId?: string
    oem?: string
    message?: string
  }
}

const SortableOemNumberItem = ({
  entry,
  index,
  vehicleGroupOptions,
  brandOptions,
  modelOptions,
  onUpdateField,
  onRemove,
  t,
  errors
}: SortableOemNumberItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  const hasError = !!(errors?.brandId || errors?.modelId || errors?.oem || errors?.message)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-stretch gap-2 rounded-md border p-2 ${hasError ? 'border-destructive' : ''}`}>
      <div {...attributes} {...listeners} className="flex cursor-grab items-center active:cursor-grabbing">
        <GripVerticalIcon className="text-muted-foreground h-5 w-5" />
      </div>
      <div className="flex items-center">
        <Badge variant={hasError ? 'destructive' : 'outline'} className="font-mono">
          {index + 1}
        </Badge>
      </div>
      <div className="flex-1 gap-2">
        <div className="grid grid-cols-1 items-start gap-2 lg:grid-cols-2 xl:grid-cols-4">
          <div className="flex flex-row gap-1">
            <Combobox
              options={vehicleGroupOptions}
              value={entry.vehicleGroupId}
              onValueChange={v => onUpdateField(index, 'vehicleGroupId', v as string)}
              placeholder={t('vehicle_group')}
              searchPlaceholder={t('search')}
              emptyText={t('not_found')}
              clearable
              error={!!errors?.vehicleGroupId}
            />
            {errors?.vehicleGroupId && (
              <p className="text-destructive text-xs" role="alert" aria-live="polite">
                {errors.vehicleGroupId}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Combobox
              options={brandOptions}
              value={entry.brandId}
              onValueChange={v => onUpdateField(index, 'brandId', v as string)}
              placeholder={t('brand')}
              searchPlaceholder={t('search')}
              emptyText={t('not_found')}
              clearable
              error={!!errors?.brandId}
            />
            {errors?.brandId && (
              <p className="text-destructive text-xs" role="alert" aria-live="polite">
                {errors.brandId}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Combobox
              options={modelOptions}
              value={entry.modelId}
              onValueChange={v => onUpdateField(index, 'modelId', v as string)}
              placeholder={t('model')}
              searchPlaceholder={t('search')}
              emptyText={entry.brandId ? t('not_found') : t('select')}
              clearable
              disabled={!entry.brandId}
              error={!!errors?.modelId}
            />
            {errors?.modelId && (
              <p className="text-destructive text-xs" role="alert" aria-live="polite">
                {errors.modelId}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Input
              placeholder={t('oem_number')}
              value={entry.oem || ''}
              onChange={e => onUpdateField(index, 'oem', e.target.value)}
              className={errors?.oem ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors?.oem && (
              <p className="text-destructive text-xs" role="alert" aria-live="polite">
                {errors.oem}
              </p>
            )}
          </div>
        </div>
        {errors?.message && (
          <p className="text-destructive text-sm" role="alert" aria-live="polite">
            {errors.message}
          </p>
        )}
      </div>
      <div className="flex items-center">
        <Button
          type="button"
          variant="destructive"
          size="icon"
          onClick={() => onRemove(index)}
          aria-label={t('remove')}>
          <MinusIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

const ProductForm = forwardRef<ProductFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const locale = useLocale()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    const currencySymbol = metaState.response?.currencies?.find(
      c => c.code === metaState.response?.generalSettings?.defaultCurrencyCode
    )?.symbol
    // Wrap the metaState.response?.locales in its own useMemo to product unnecessary re-renders
    const localesData = useMemo(() => metaState.response?.locales, [metaState.response])
    const locales = useMemo(() => localesData || [], [localesData])
    // Find the locale object that matches the current locale
    const currentLocaleObj = locales.find((l: any) => l.fullCode === locale || l.code === locale)

    // State for pictures, files
    const [pictures, setPictures] = useState<FileType[]>(initialData?.pictures?.map(p => p.file) || [])
    const [files, setFiles] = useState<FileType[]>(initialData?.files?.map(f => f.file) || [])

    // State for the selected category to enable dynamic validation
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>(initialData?.category?.id || '')

    // Store original boxDimensionalWeight from backend (before being cleared by dimensions)
    const originalBoxDimensionalWeight = useMemo(() => {
      return initialData?.boxDimensionalWeight !== undefined && initialData?.boxDimensionalWeight !== null
        ? String(initialData.boxDimensionalWeight)
        : ''
    }, [initialData?.boxDimensionalWeight])

    // Fetch category data for dynamic property validation
    const { data: categoryData } = useCategory(selectedCategoryId)
    const { data: vehicleGroupsData } = useVehicleGroups({ size: 999999 })
    const { data: brandsData } = useBrands({ size: 999999 })

    // Prepare brand options for combobox
    const vehicleGroupOptions = useMemo(() => {
      if (!vehicleGroupsData?.items) {
        return []
      }

      return vehicleGroupsData.items.map(vehicleGroup => {
        let title
        const firstTranslation = Object.values(vehicleGroup.translations)[0] || 'id'
        if (currentLocaleObj && vehicleGroup.translations[currentLocaleObj.fullCode]) {
          title = vehicleGroup.translations[currentLocaleObj.fullCode].title || firstTranslation.title
        } else {
          const firstTranslation = Object.values(vehicleGroup.translations)[0]
          title = firstTranslation.title
        }

        return {
          label: title,
          value: vehicleGroup.id,
          searchValue: title
        }
      })
    }, [currentLocaleObj, vehicleGroupsData])

    // Prepare brand options for combobox
    const brandOptions = useMemo(() => {
      if (!brandsData?.items) {
        return []
      }
      return brandsData.items.map(brand => ({
        label: brand.name,
        value: brand.id,
        searchValue: brand.name
      }))
    }, [brandsData])

    // Wrapper functions to handle type conversion for FileSelector onChange
    const handlePicturesChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setPictures([])
      } else if ('length' in files) {
        setPictures(files)
      } else {
        setPictures([files])
      }
    }

    const handleFilesChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setFiles([])
      } else if ('length' in files) {
        setFiles(files)
      } else {
        setFiles([files])
      }
    }

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const hasLocaleError = (localeCode: string, field: keyof ProductTranslationParam): boolean => {
      return !!formErrors.translations?.[localeCode]?.[field]
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<string, z.ZodType<ProductTranslationParam>> = {}

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          title: z.string().min(1, t('not_blank')),
          content: z.string().optional()
        })
      })

      return z
        .object({
          categoryId: z.string().min(1, t('not_blank')),
          additionalCategoryIds: z.array(z.string()),
          code: z.string().min(1, t('not_blank')),
          oemNumbers: z.array(
            z.object({
              id: z.string(),
              vehicleGroupId: z.string().min(1, t('not_blank')),
              brandId: z.string().min(1, t('not_blank')),
              modelId: z.string().min(1, t('not_blank')),
              oem: z.string().min(1, t('not_blank'))
            })
          ),
          price: z
            .string()
            .min(1, t('not_blank'))
            .refine(v => !Number.isNaN(Number(v)), t('invalid_number')),
          taxRate: z
            .string()
            .min(1, t('not_blank'))
            .refine(v => !Number.isNaN(Number(v)), t('invalid_number')),
          priceIncludesTax: z.boolean(),
          discountRate: z
            .string()
            .optional()
            .refine(v => v === undefined || v === '' || !Number.isNaN(Number(v)), t('invalid_number'))
            .refine(v => v === undefined || v === '' || (Number(v) >= 0 && Number(v) <= 100), t('invalid_number')),
          stock: z
            .string()
            .min(1, t('not_blank'))
            .refine(v => !Number.isNaN(Number(v)), t('invalid_number')),
          weight: z
            .string()
            .optional()
            .refine(v => v === undefined || v === '' || !Number.isNaN(Number(v)), t('invalid_number')),
          unitsPerBox: z
            .string()
            .optional()
            .refine(
              v => v === undefined || v === '' || (!Number.isNaN(Number(v)) && Number.isInteger(Number(v))),
              t('invalid_number')
            ),
          boxLength: z
            .string()
            .optional()
            .refine(v => v === undefined || v === '' || !Number.isNaN(Number(v)), t('invalid_number')),
          boxWidth: z
            .string()
            .optional()
            .refine(v => v === undefined || v === '' || !Number.isNaN(Number(v)), t('invalid_number')),
          boxHeight: z
            .string()
            .optional()
            .refine(v => v === undefined || v === '' || !Number.isNaN(Number(v)), t('invalid_number')),
          boxDimensionalWeight: z
            .string()
            .optional()
            .refine(v => v === undefined || v === '' || !Number.isNaN(Number(v)), t('invalid_number')),
          active: z.boolean(),
          translations: z.object(translationsSchema),
          properties: z.array(
            z.object({
              id: z.string(),
              fields: z.array(
                z.object({
                  id: z.string(),
                  value: z.string()
                })
              )
            })
          ),
          repairKitIds: z.array(z.string()),
          repairForIds: z.array(z.string())
        })
        .superRefine((data, ctx) => {
          const hasLength = data.boxLength && data.boxLength.trim() !== ''
          const hasWidth = data.boxWidth && data.boxWidth.trim() !== ''
          const hasHeight = data.boxHeight && data.boxHeight.trim() !== ''

          // If anyone is filled, all three `must be` filled
          const anyFilled = hasLength || hasWidth || hasHeight

          if (anyFilled) {
            // Show error on empty fields
            if (!hasLength) {
              ctx.addIssue({
                code: 'custom',
                message: t('not_blank'),
                path: ['boxLength']
              })
            }
            if (!hasWidth) {
              ctx.addIssue({
                code: 'custom',
                message: t('not_blank'),
                path: ['boxWidth']
              })
            }
            if (!hasHeight) {
              ctx.addIssue({
                code: 'custom',
                message: t('not_blank'),
                path: ['boxHeight']
              })
            }
          }
        })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: ProductTranslationsParam = {}

      locales.forEach(locale => {
        // Ensure values are always strings, never undefined
        const translation = initialData?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          title: translation?.title ?? '',
          content: translation?.content ?? ''
        }
      })

      return {
        categoryId: initialData?.category?.id ?? '',
        additionalCategoryIds: initialData?.additionalCategories?.map(c => c.id) || [],
        code: initialData?.code || '',
        oemNumbers:
          initialData?.oemNumbers?.map((on, index) => ({
            id: on.id || `oem-${index}-${Date.now()}`,
            vehicleGroupId: on.vehicleGroup?.id || '',
            brandId: on.model?.brand?.id || '',
            modelId: on.model?.id || '',
            oem: on.oem || ''
          })) || [],
        active: initialData?.active || true,
        price:
          initialData?.price !== null
            ? String(
                initialData?.priceIncludesTax
                  ? (initialData?.price.originalGross ?? initialData?.price.gross)
                  : (initialData?.price.originalNet ?? initialData?.price.net)
              )
            : '',
        taxRate:
          initialData?.taxRate !== undefined && initialData?.taxRate !== null
            ? String(initialData.taxRate)
            : metaState.response?.generalSettings?.defaultTaxRate !== undefined &&
                metaState.response.generalSettings.defaultTaxRate !== null
              ? String(metaState.response.generalSettings.defaultTaxRate)
              : '0',
        priceIncludesTax: initialData?.priceIncludesTax ?? false,
        discountRate:
          initialData?.discountRate !== undefined && initialData?.discountRate !== null
            ? String(initialData.discountRate)
            : '0',
        stock: initialData?.stock !== undefined && initialData?.stock !== null ? String(initialData.stock) : '',
        weight: initialData?.weight !== undefined && initialData?.weight !== null ? String(initialData.weight) : '',
        unitsPerBox:
          initialData?.unitsPerBox !== undefined && initialData?.unitsPerBox !== null
            ? String(initialData.unitsPerBox)
            : '',
        boxLength:
          initialData?.boxLength !== undefined && initialData?.boxLength !== null ? String(initialData.boxLength) : '',
        boxWidth:
          initialData?.boxWidth !== undefined && initialData?.boxWidth !== null ? String(initialData.boxWidth) : '',
        boxHeight:
          initialData?.boxHeight !== undefined && initialData?.boxHeight !== null ? String(initialData.boxHeight) : '',
        // If all dimensions exist, prioritize them and clear boxDimensionalWeight
        // Otherwise, use boxDimensionalWeight if it exists
        boxDimensionalWeight: (() => {
          const hasAllDimensions =
            initialData?.boxLength !== undefined &&
            initialData?.boxLength !== null &&
            initialData?.boxWidth !== undefined &&
            initialData?.boxWidth !== null &&
            initialData?.boxHeight !== undefined &&
            initialData?.boxHeight !== null

          if (hasAllDimensions) {
            return ''
          }

          return initialData?.boxDimensionalWeight !== undefined && initialData?.boxDimensionalWeight !== null
            ? String(initialData.boxDimensionalWeight)
            : ''
        })(),
        translations: defaultTranslations,
        properties:
          initialData?.properties?.map(prop => ({
            id: prop.categoryPropertyId,
            fields: prop.fields.map(field => ({
              id: field.id,
              value: field.value || ''
            }))
          })) || [],
        repairKitIds: initialData?.repairKits?.map(kit => kit.id) || [],
        repairForIds: initialData?.repairedProducts?.map(product => product.id) || []
      }
    }, [locales, initialData, metaState.response?.generalSettings?.defaultTaxRate])

    const form = useForm<FormData>({
      resolver: zodResolver(schema) as any,
      defaultValues: getDefaultValues(),
      mode: 'onChange'
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Update form values when initialData or locales change
    useEffect(() => {
      if (locales.length > 0) {
        const newDefaultValues = getDefaultValues()
        form.reset(newDefaultValues)
      }
    }, [getDefaultValues, locales, form])

    // Sync pictures and files state when initialData changes
    useEffect(() => {
      if (initialData?.pictures) {
        setPictures(initialData.pictures.map(p => p.file))
      }
      if (initialData?.files) {
        setFiles(initialData.files.map(f => f.file))
      }
    }, [initialData])

    const formErrors = form.formState.errors

    // Watch categoryId for category properties
    const watchedCategoryId = form.watch('categoryId')

    // Sync selectedCategoryId with watchedCategoryId for dynamic validation
    useEffect(() => {
      if (watchedCategoryId !== selectedCategoryId) {
        setSelectedCategoryId(watchedCategoryId || '')
      }
    }, [watchedCategoryId, selectedCategoryId])

    // OEM Numbers dynamic list helpers
    const watchedOemNumbers = form.watch('oemNumbers')
    const oemNumbersValues: OemNumberEntry[] = useMemo(() => watchedOemNumbers || [], [watchedOemNumbers])

    // Get unique selected brand IDs from oemNumbers for fetching models
    const selectedBrandIds = useMemo(() => {
      const brandIds = oemNumbersValues.map(on => on.brandId).filter(Boolean)
      return [...new Set(brandIds)]
    }, [oemNumbersValues])

    // Fetch models only when there are selected brands
    const shouldFetchModels = selectedBrandIds.length > 0
    const { data: modelsData } = useModels({ size: 999999, brandIds: selectedBrandIds }, { enabled: shouldFetchModels })

    // Prepare model options for combobox, grouped by brand
    const getModelOptionsForBrand = useCallback(
      (brandId: string) => {
        if (!modelsData?.items || !brandId) {
          return []
        }
        return modelsData.items
          .filter(model => model.brand?.id === brandId)
          .map(model => ({
            label: model.name,
            value: model.id,
            searchValue: model.name
          }))
      },
      [modelsData]
    )

    const addOemNumber = () => {
      const next = [
        ...oemNumbersValues,
        { id: `oem-${Date.now()}`, vehicleGroupId: '', brandId: '', modelId: '', oem: '' }
      ]
      form.setValue('oemNumbers', next, { shouldDirty: true, shouldTouch: true })
    }
    const updateOemNumberAt = (index: number, field: keyof Omit<OemNumberEntry, 'id'>, value: string) => {
      const next = [...oemNumbersValues]
      next[index] = { ...next[index], [field]: value }
      // If brand changes, reset model
      if (field === 'brandId') {
        next[index].modelId = ''
      }
      form.setValue('oemNumbers', next, { shouldDirty: true, shouldTouch: true })
    }
    const removeOemNumberAt = (index: number) => {
      const next = [...oemNumbersValues]
      next.splice(index, 1)
      form.setValue('oemNumbers', next, { shouldDirty: true, shouldTouch: true })
    }

    // Handle drag end for OEM Numbers reordering
    const handleOemNumberDragEnd = (event: DragEndEvent) => {
      const { active, over } = event
      if (over && active.id !== over.id) {
        const oldIndex = oemNumbersValues.findIndex(item => item.id === active.id)
        const newIndex = oemNumbersValues.findIndex(item => item.id === over.id)
        const reordered = arrayMove(oemNumbersValues, oldIndex, newIndex)
        form.setValue('oemNumbers', reordered, { shouldDirty: true, shouldTouch: true })
      }
    }

    // Set up sensors for drag and drop
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8
        }
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    )

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    // Handle form submission
    const handleSubmit: SubmitHandler<FormData> = useCallback(
      async data => {
        const picturesData: ProductPictureParams[] = pictures.map((file, index) => ({
          fileId: file.id,
          order: index + 1,
          isMain: !!file.isMain
        }))

        const filesData: ProductFileParams[] = files.map((file, index) => ({
          fileId: file.id,
          order: index + 1
        }))

        const oemNumbersData: ProductOemParams[] = (data.oemNumbers || [])
          .filter(on => on.modelId && on.oem.trim())
          .map((on, index) => ({
            vehicleGroupId: on.vehicleGroupId,
            modelId: on.modelId,
            oem: on.oem.trim(),
            order: index + 1
          }))

        const propertiesData: ProductPropertyParams[] = (data.properties || []).map(prop => ({
          id: prop.id,
          fields: prop.fields.map(field => ({
            id: field.id,
            value: field.value
          }))
        }))

        // Check which method to use: dimensions or dimensional weight
        const hasBoxLength = data.boxLength && data.boxLength.trim() !== ''
        const hasBoxWidth = data.boxWidth && data.boxWidth.trim() !== ''
        const hasBoxHeight = data.boxHeight && data.boxHeight.trim() !== ''
        const hasAllDimensions = hasBoxLength && hasBoxWidth && hasBoxHeight
        const hasBoxDimensionalWeight = data.boxDimensionalWeight && data.boxDimensionalWeight.trim() !== ''

        const submissionData: CreateProductParams = {
          categoryId: data.categoryId,
          additionalCategoryIds: data.additionalCategoryIds || [],
          code: data.code,
          price: Number(data.price),
          taxRate: Number(data.taxRate),
          priceIncludesTax: data.priceIncludesTax,
          discountRate: data.discountRate === '' ? 0 : Number(data.discountRate),
          stock: Number(data.stock),
          ...(data.weight && data.weight.trim() !== '' && { weight: Number(data.weight) }),
          ...(data.unitsPerBox && data.unitsPerBox.trim() !== '' && { unitsPerBox: Number(data.unitsPerBox) }),
          ...(hasAllDimensions && {
            boxLength: Number(data.boxLength),
            boxWidth: Number(data.boxWidth),
            boxHeight: Number(data.boxHeight),
            ...(mode === FormModeEnum.UPDATE && { boxDimensionalWeight: '' })
          }),
          ...(hasBoxDimensionalWeight &&
            !hasAllDimensions && {
              boxDimensionalWeight: Number(data.boxDimensionalWeight),
              ...(mode === FormModeEnum.UPDATE && { boxLength: '', boxWidth: '', boxHeight: '' })
            }),
          ...(!hasAllDimensions &&
            !hasBoxDimensionalWeight &&
            mode === FormModeEnum.UPDATE && {
              boxLength: '',
              boxWidth: '',
              boxHeight: '',
              boxDimensionalWeight: ''
            }),
          active: data.active,
          translations: {},
          pictures: picturesData,
          files: filesData,
          oemNumbers: oemNumbersData,
          properties: propertiesData,
          repairKitIds: data.repairKitIds || [],
          repairForIds: data.repairForIds || []
        }

        locales.forEach(locale => {
          submissionData.translations[locale.fullCode] = {
            title: data.translations[locale.fullCode]?.title || '',
            content: data.translations[locale.fullCode]?.content || ''
          }
        })

        onSubmit(submissionData)
      },
      [pictures, files, locales, onSubmit, mode]
    )

    const handleSubmitAndNew: SubmitHandler<FormData> = useCallback(
      async data => {
        const picturesData: ProductPictureParams[] = pictures.map((file, index) => ({
          fileId: file.id,
          order: index + 1,
          isMain: !!file.isMain
        }))

        const filesData: ProductFileParams[] = files.map((file, index) => ({
          fileId: file.id,
          order: index + 1
        }))

        const oemNumbersData: ProductOemParams[] = (data.oemNumbers || [])
          .filter(on => on.modelId && on.oem.trim())
          .map((on, index) => ({
            vehicleGroupId: on.vehicleGroupId,
            modelId: on.modelId,
            oem: on.oem.trim(),
            order: index + 1
          }))

        const propertiesData: ProductPropertyParams[] = (data.properties || []).map(prop => ({
          id: prop.id,
          fields: prop.fields.map(field => ({
            id: field.id,
            value: field.value
          }))
        }))

        const hasBoxLength = data.boxLength && data.boxLength.trim() !== ''
        const hasBoxWidth = data.boxWidth && data.boxWidth.trim() !== ''
        const hasBoxHeight = data.boxHeight && data.boxHeight.trim() !== ''
        const hasAllDimensions = hasBoxLength && hasBoxWidth && hasBoxHeight
        const hasBoxDimensionalWeight = data.boxDimensionalWeight && data.boxDimensionalWeight.trim() !== ''

        const submissionData: CreateProductParams = {
          categoryId: data.categoryId,
          additionalCategoryIds: data.additionalCategoryIds || [],
          code: data.code,
          price: Number(data.price),
          taxRate: Number(data.taxRate),
          priceIncludesTax: data.priceIncludesTax,
          discountRate: data.discountRate === '' ? 0 : Number(data.discountRate),
          stock: Number(data.stock),
          ...(data.weight && data.weight.trim() !== '' && { weight: Number(data.weight) }),
          ...(data.unitsPerBox && data.unitsPerBox.trim() !== '' && { unitsPerBox: Number(data.unitsPerBox) }),
          ...(hasAllDimensions && {
            boxLength: Number(data.boxLength),
            boxWidth: Number(data.boxWidth),
            boxHeight: Number(data.boxHeight),
            ...(mode === FormModeEnum.UPDATE && { boxDimensionalWeight: '' })
          }),
          ...(hasBoxDimensionalWeight &&
            !hasAllDimensions && {
              boxDimensionalWeight: Number(data.boxDimensionalWeight),
              ...(mode === FormModeEnum.UPDATE && { boxLength: '', boxWidth: '', boxHeight: '' })
            }),
          ...(!hasAllDimensions &&
            !hasBoxDimensionalWeight &&
            mode === FormModeEnum.UPDATE && {
              boxLength: '',
              boxWidth: '',
              boxHeight: '',
              boxDimensionalWeight: ''
            }),
          active: data.active,
          translations: {},
          pictures: picturesData,
          files: filesData,
          oemNumbers: oemNumbersData,
          properties: propertiesData,
          repairKitIds: data.repairKitIds || [],
          repairForIds: data.repairForIds || []
        }

        locales.forEach(locale => {
          submissionData.translations[locale.fullCode] = {
            title: data.translations[locale.fullCode]?.title || '',
            content: data.translations[locale.fullCode]?.content || ''
          }
        })

        onSubmitAndNew?.(submissionData)
      },
      [pictures, files, locales, onSubmitAndNew, mode]
    )

    const submitFormWithValidation = useCallback(
      async (submitHandler: SubmitHandler<FormData>) => {
        const isZodValid = await form.trigger()

        let isPropertiesValid = true
        let currentProperties = form.getValues('properties') || []

        if (currentProperties.length === 0 && categoryData?.properties && categoryData.properties.length > 0) {
          currentProperties = categoryData.properties.map(prop => ({
            id: prop.id,
            fields: prop.property.fields.map(field => ({
              id: field.id,
              value: ''
            }))
          }))
          form.setValue('properties', currentProperties)
        }

        if (categoryData?.properties) {
          categoryData.properties.forEach(categoryProp => {
            const propertyData = currentProperties.find((p: PropertyFieldsData) => p.id === categoryProp.id)

            if (!propertyData) {
              return
            }

            categoryProp.property.fields.forEach(field => {
              if (field.required) {
                const fieldData = propertyData.fields.find((f: { id: string; value: string }) => f.id === field.id)

                if (!fieldData || !fieldData.value || fieldData.value.trim() === '') {
                  const actualPropIndex = currentProperties.findIndex(
                    (p: PropertyFieldsData) => p.id === categoryProp.id
                  )
                  const actualFieldIndex = propertyData.fields.findIndex(
                    (f: { id: string; value: string }) => f.id === field.id
                  )

                  if (actualPropIndex !== -1 && actualFieldIndex !== -1) {
                    form.setError(`properties.${actualPropIndex}.fields.${actualFieldIndex}.value`, {
                      type: 'manual',
                      message: t('not_blank')
                    })
                    isPropertiesValid = false
                  }
                }
              }
            })
          })
        }

        if (!isZodValid || !isPropertiesValid) {
          scrollToFirstError()
          return
        }

        await form.handleSubmit(submitHandler as any, scrollToFirstError)()
      },
      [form, categoryData, t, scrollToFirstError]
    )

    const onFormSubmit = useCallback(
      async (e: FormEvent) => {
        e.preventDefault()
        await submitFormWithValidation(handleSubmit)
      },
      [handleSubmit, submitFormWithValidation]
    )

    const onFormSubmitAndNew = useCallback(
      async (e: FormEvent) => {
        e.preventDefault()
        await submitFormWithValidation(handleSubmitAndNew)
      },
      [handleSubmitAndNew, submitFormWithValidation]
    )

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    if (!metaState.response?.locales) {
      return (
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* General Information */}
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-1 lg:grid-cols-2">
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            <Separator />

            {/* Category & Code */}
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-1 lg:grid-cols-2">
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            <Separator />

            {/* Price & Stock */}
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-1 lg:grid-cols-2">
              <div className="space-y-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            <Separator />

            {/* Translations */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-24 w-full" />
                </div>
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <Form {...form}>
        <form onSubmit={isCreateModeWithSubmitAndNew ? onFormSubmitAndNew : onFormSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PackageIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_product') : t('edit_product')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* General information */}
              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-1 lg:grid-cols-3">
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required className="flex items-center gap-2">
                        {t('category')}
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          value={field.value}
                          onValueChange={v => field.onChange(v)}
                          disabled={isLoading}
                          clearable
                          error={!!formErrors.categoryId}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="additionalCategoryIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">{t('additional_categories')}</FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          value={field.value || []}
                          onValueChange={v => field.onChange(v)}
                          disabled={isLoading}
                          clearable
                          multiple
                          excludeId={watchedCategoryId}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('code')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('code')} {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 xl:grid-cols-6">
                <FormField
                  control={form.control}
                  name="stock"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('stock')}</FormLabel>
                      <FormControl className="items-start">
                        <NumberInput
                          placeholder={t('stock')}
                          min={0}
                          stepper={1}
                          value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                          onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('price')}</FormLabel>
                      <FormControl className="items-start">
                        <MoneyInput
                          currencySymbol={currencySymbol}
                          placeholder={t('price')}
                          value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                          onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="taxRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('tax_rate')}</FormLabel>
                      <FormControl className="items-start">
                        <NumberInput
                          placeholder="0.00"
                          min={0}
                          max={100}
                          decimalScale={2}
                          stepper={0.01}
                          suffix="%"
                          value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                          onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="discountRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('product_discount_rate')}</FormLabel>
                      <FormControl>
                        <NumberInput
                          placeholder="0.00"
                          min={0}
                          max={100}
                          decimalScale={2}
                          stepper={0.01}
                          suffix="%"
                          value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                          onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                        />
                      </FormControl>
                      <FormDescription>{t('product_discount_rate_hint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priceIncludesTax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('price_includes_tax')}</FormLabel>
                      <div className="mt-2 flex flex-row items-center gap-2">
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <span className="text-muted-foreground text-sm">{field.value ? t('yes') : t('no')}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* OEM Numbers list */}
              <div className="space-y-2">
                <FormLabel className="flex items-center gap-2">{t('oem_numbers')}</FormLabel>
                <div className="space-y-2">
                  {oemNumbersValues.length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t('no_oem_numbers')}</p>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOemNumberDragEnd}>
                      <SortableContext
                        items={oemNumbersValues.map(item => item.id)}
                        strategy={verticalListSortingStrategy}>
                        <div className="flex flex-col gap-2">
                          {oemNumbersValues.map((entry, idx) => {
                            const oemErrors = formErrors.oemNumbers?.[idx]
                            const errorMessage = oemErrors?.message
                            return (
                              <SortableOemNumberItem
                                key={entry.id}
                                entry={entry}
                                index={idx}
                                vehicleGroupOptions={vehicleGroupOptions}
                                brandOptions={brandOptions}
                                modelOptions={getModelOptionsForBrand(entry.brandId)}
                                onUpdateField={updateOemNumberAt}
                                onRemove={removeOemNumberAt}
                                t={t}
                                errors={
                                  oemErrors
                                    ? {
                                        vehicleGroupId: oemErrors.vehicleGroupId?.message,
                                        brandId: oemErrors.brandId?.message,
                                        modelId: oemErrors.modelId?.message,
                                        oem: oemErrors.oem?.message,
                                        message:
                                          typeof errorMessage === 'string'
                                            ? errorMessage
                                            : (errorMessage as any)?.message
                                      }
                                    : undefined
                                }
                              />
                            )
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={addOemNumber}>
                    <PlusIcon className="h-4 w-4" />
                    {t('add')}
                  </Button>
                </div>
              </div>

              {/* Category Properties */}
              {watchedCategoryId && (
                <PropertyFieldsForm
                  categoryId={watchedCategoryId}
                  form={form}
                  fieldName="properties"
                  initialData={
                    initialData?.properties?.map(prop => ({
                      id: prop.categoryPropertyId,
                      fields: prop.fields.map(field => ({
                        id: field.id,
                        value: field.value || ''
                      }))
                    })) || []
                  }
                />
              )}

              <Separator />

              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.title"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError =
                          hasLocaleError(locale.fullCode, 'title') || hasLocaleError(locale.fullCode, 'content')

                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasTitleError = hasLocaleError(locale.fullCode, 'title')
                      const hasContentError = hasLocaleError(locale.fullCode, 'content')
                      const hasError = hasTitleError || hasContentError

                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.title`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('title')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('title')}
                                    className={`${hasTitleError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.content`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('content')}</FormLabel>
                                <FormControl>
                                  <Editor
                                    placeholder={t('content')}
                                    value={field.value || ''}
                                    autoFocus={false}
                                    onChange={html => field.onChange(html)}
                                    isError={hasContentError}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p>{t('no_locales_available')}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Measurements and Packaging */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <PencilRulerIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('measurements_and_packaging')}</h3>
                </div>
                <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="weight"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('weight')} (kg)</FormLabel>
                        <FormControl>
                          <NumberInput
                            placeholder={t('weight')}
                            min={0}
                            decimalScale={2}
                            stepper={0.01}
                            value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                            onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unitsPerBox"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('units_per_box')}</FormLabel>
                        <FormControl>
                          <NumberInput
                            placeholder={t('units_per_box')}
                            min={0}
                            stepper={1}
                            value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                            onValueChange={v => field.onChange(v === undefined ? '' : String(v))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="boxLength"
                    render={({ field }) => {
                      const boxDimensionalWeight = form.watch('boxDimensionalWeight')
                      const isDisabled = Boolean(boxDimensionalWeight && boxDimensionalWeight.trim() !== '')

                      return (
                        <FormItem>
                          <FormLabel>{t('box_length')} (cm)</FormLabel>
                          <FormControl>
                            <NumberInput
                              placeholder={t('box_length')}
                              min={0}
                              decimalScale={2}
                              stepper={0.01}
                              disabled={isDisabled}
                              value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                              onValueChange={v => {
                                field.onChange(v === undefined ? '' : String(v))

                                const currentBoxWidth = form.getValues('boxWidth')
                                const currentBoxHeight = form.getValues('boxHeight')

                                // Clear boxDimensionalWeight if any dimension is filled
                                if (v !== undefined) {
                                  form.setValue('boxDimensionalWeight', '')
                                } else {
                                  // If all three dimensions are now empty, restore the original boxDimensionalWeight
                                  const allEmpty =
                                    (!currentBoxWidth || currentBoxWidth.trim() === '') &&
                                    (!currentBoxHeight || currentBoxHeight.trim() === '')
                                  if (allEmpty && originalBoxDimensionalWeight) {
                                    form.setValue('boxDimensionalWeight', originalBoxDimensionalWeight)
                                  }
                                }

                                // Trigger validation for all box dimension fields
                                setTimeout(async () => {
                                  await form.trigger(['boxLength', 'boxWidth', 'boxHeight'])
                                }, 0)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="boxWidth"
                    render={({ field }) => {
                      const boxDimensionalWeight = form.watch('boxDimensionalWeight')
                      const isDisabled = Boolean(boxDimensionalWeight && boxDimensionalWeight.trim() !== '')

                      return (
                        <FormItem>
                          <FormLabel>{t('box_width')} (cm)</FormLabel>
                          <FormControl>
                            <NumberInput
                              placeholder={t('box_width')}
                              min={0}
                              decimalScale={2}
                              stepper={0.01}
                              disabled={isDisabled}
                              value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                              onValueChange={v => {
                                field.onChange(v === undefined ? '' : String(v))

                                const currentBoxLength = form.getValues('boxLength')
                                const currentBoxHeight = form.getValues('boxHeight')

                                // Clear boxDimensionalWeight if any dimension is filled
                                if (v !== undefined) {
                                  form.setValue('boxDimensionalWeight', '')
                                } else {
                                  // If all three dimensions are now empty, restore the original boxDimensionalWeight
                                  const allEmpty =
                                    (!currentBoxLength || currentBoxLength.trim() === '') &&
                                    (!currentBoxHeight || currentBoxHeight.trim() === '')
                                  if (allEmpty && originalBoxDimensionalWeight) {
                                    form.setValue('boxDimensionalWeight', originalBoxDimensionalWeight)
                                  }
                                }

                                // Trigger validation for all box dimension fields
                                setTimeout(async () => {
                                  await form.trigger(['boxLength', 'boxWidth', 'boxHeight'])
                                }, 0)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="boxHeight"
                    render={({ field }) => {
                      const boxDimensionalWeight = form.watch('boxDimensionalWeight')
                      const isDisabled = Boolean(boxDimensionalWeight && boxDimensionalWeight.trim() !== '')

                      return (
                        <FormItem>
                          <FormLabel>{t('box_height')} (cm)</FormLabel>
                          <FormControl>
                            <NumberInput
                              placeholder={t('box_height')}
                              min={0}
                              decimalScale={2}
                              stepper={0.01}
                              disabled={isDisabled}
                              value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                              onValueChange={v => {
                                field.onChange(v === undefined ? '' : String(v))

                                const currentBoxLength = form.getValues('boxLength')
                                const currentBoxWidth = form.getValues('boxWidth')

                                // Clear boxDimensionalWeight if any dimension is filled
                                if (v !== undefined) {
                                  form.setValue('boxDimensionalWeight', '')
                                } else {
                                  // If all three dimensions are now empty, restore the original boxDimensionalWeight
                                  const allEmpty =
                                    (!currentBoxLength || currentBoxLength.trim() === '') &&
                                    (!currentBoxWidth || currentBoxWidth.trim() === '')
                                  if (allEmpty && originalBoxDimensionalWeight) {
                                    form.setValue('boxDimensionalWeight', originalBoxDimensionalWeight)
                                  }
                                }

                                // Trigger validation for all box dimension fields
                                setTimeout(async () => {
                                  await form.trigger(['boxLength', 'boxWidth', 'boxHeight'])
                                }, 0)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="boxDimensionalWeight"
                    render={({ field }) => {
                      const boxLength = form.watch('boxLength')
                      const boxWidth = form.watch('boxWidth')
                      const boxHeight = form.watch('boxHeight')
                      const hasDimensions = Boolean(
                        (boxLength && boxLength.trim() !== '') ||
                        (boxWidth && boxWidth.trim() !== '') ||
                        (boxHeight && boxHeight.trim() !== '')
                      )
                      const allDimensionsFilled = Boolean(
                        boxLength &&
                        boxLength.trim() !== '' &&
                        boxWidth &&
                        boxWidth.trim() !== '' &&
                        boxHeight &&
                        boxHeight.trim() !== ''
                      )

                      return (
                        <FormItem>
                          <FormLabel>{t('box_dimensional_weight')}</FormLabel>
                          <FormControl>
                            <NumberInput
                              placeholder={t('box_dimensional_weight')}
                              min={0}
                              decimalScale={2}
                              stepper={0.01}
                              disabled={hasDimensions}
                              value={field.value === '' || field.value === undefined ? undefined : Number(field.value)}
                              onValueChange={v => {
                                field.onChange(v === undefined ? '' : String(v))
                                // Clear dimensions if boxDimensionalWeight is filled
                                if (v !== undefined) {
                                  form.setValue('boxLength', '')
                                  form.setValue('boxWidth', '')
                                  form.setValue('boxHeight', '')
                                }
                              }}
                            />
                          </FormControl>
                          {allDimensionsFilled && (
                            <p className="text-muted-foreground text-sm">
                              {t('box_dimensional_weight_auto_calculated')}
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-1 lg:grid-cols-2">
                {/* Repair Kits Selection */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <ToolboxIcon className="mt-0.5 h-5 w-5" />
                      <h3 className="font-semibold">{t('repair_kits_selection')}</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">{t('repair_kits_selection_description')}</p>
                  </div>
                  <FormField
                    control={form.control}
                    name="repairKitIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('repair_kits')}</FormLabel>
                        <FormControl>
                          <ProductCombobox
                            value={field.value}
                            onValueChange={field.onChange}
                            multiple
                            clearable
                            disabled={isLoading}
                            excludeId={initialData?.id}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Repaired Products Selection */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <ToolboxIcon className="mt-0.5 h-5 w-5" />
                      <h3 className="font-semibold">{t('repaired_products_selection')}</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">{t('repaired_products_selection_description')}</p>
                  </div>
                  <FormField
                    control={form.control}
                    name="repairForIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('repaired_kits')}</FormLabel>
                        <FormControl>
                          <ProductCombobox
                            value={field.value}
                            onValueChange={field.onChange}
                            multiple
                            clearable
                            disabled={isLoading}
                            excludeId={initialData?.id}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Pictures */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('pictures')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={true}
                    onlyImages={true}
                    value={pictures}
                    onChange={handlePicturesChange}
                    buttonLabel={t('select_pictures')}
                    buttonLabelChange={t('change_pictures')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={initialData?.mainPicture?.id}
                  />
                </div>
              </div>

              <Separator />

              {/* Files */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FileIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('files')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={true}
                    value={files}
                    onChange={handleFilesChange}
                    buttonLabel={t('select_files')}
                    buttonLabelChange={t('change_files')}
                    noSelectionText={t('no_files_selected')}
                  />
                </div>
              </div>

              <Separator />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-start justify-start">
                    <div className="space-y-0.5">
                      <FormLabel>{t('active')}</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => void submitFormWithValidation(handleSubmit)}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">
                      {isLoading || form.formState.isSubmitting
                        ? t('saving')
                        : isCreateModeWithSubmitAndNew
                          ? t('save_and_add_new')
                          : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

ProductForm.displayName = 'ProductForm'

export default ProductForm
